/**
 * The admin wordlist panel's domain models — camelCase, projected from the snake_case wire shapes in
 * `backend-types/wordlist-admin-api.ts`. Projection lives in `domain/adminWordlistMappers.ts`.
 *
 * Deliberately a separate file from `domain/admin.ts`: these routes live on a different worker
 * (`getWordlistApiUrl()`, not `getWorkerApiUrl()`) with their own admin gate, and the wordlist
 * catalog isn't part of the user/signup/email/transcription surface that file already covers.
 */

import type {AdminPage} from './admin';

export interface AdminWordlist {
  id: string;
  /** `public.users.id` — the same id `/admin/users/:id` takes. */
  userId: string;
  name: string;
  description: string | null;
  wordCount: number;
  isPublic: boolean;
  isOfficial: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminWordlistOwner {
  id: string;
  email: string;
  name: string | null;
}

/** The detail screen's shape: everything the list row has, plus the words and the owner. */
export interface AdminWordlistDetail extends AdminWordlist {
  words: string[];
  /** Null when the owner row is missing or was itself deleted — best-effort, not an error. */
  owner: AdminWordlistOwner | null;
}

export type {AdminPage};

/* ==========================================================================
 * Query parameters
 * ========================================================================== */

export type AdminWordlistSortOrder = 'asc' | 'desc';

/** Three-way toggle for a boolean filter: '' means "don't filter on this at all". */
export type AdminTriState = 'true' | 'false' | '';

export interface AdminWordlistQuery {
  search?: string;
  isOfficial?: AdminTriState;
  isPublic?: AdminTriState;
  userId?: string;
  includeDeleted?: boolean;
  /** Orders by createdAt. */
  order?: AdminWordlistSortOrder;
  limit?: number;
  offset?: number;
}

export interface AdminWordlistCreateInput {
  name: string;
  description?: string;
  words?: string[];
  isPublic?: boolean;
  isOfficial?: boolean;
  /** Defaults server-side to the calling admin when omitted. */
  userId?: string;
}

export interface AdminWordlistUpdateInput {
  id: string;
  name?: string;
  description?: string;
  words?: string[];
  isPublic?: boolean;
  isOfficial?: boolean;
  /** `false` restores a soft-deleted list. */
  isDeleted?: boolean;
}

/** Default page size. The endpoint's own default is 50, its cap is 200. */
export const ADMIN_WORDLISTS_PAGE_SIZE = 50;
